import {Types} from "../types";

export const mapKeys = (obj: object | null, fn: (k: string, a: any) => string) => {
    if (obj == null) {
        return null;
    }

    const ret = {};
    for (const k in obj) {
        ret[fn(k, obj[k])] = obj[k];
    }
    return ret;
};

export const filterKeys = <T,>(map:T, fn: (key:keyof T, val:T[typeof key]) => boolean):Partial<T> => {
    return arrMapToO(
        Object.keys(map).filter((key) => fn(key as keyof T, map[key])),
        (k) => map[k],
    );
};

export const mapValues = <T, Map extends (a: T[typeof k], k: keyof T) => any>(obj:T, fn:Map): {[key in keyof T]: ReturnType<Map>} => {
    if (obj == null) {
        return null;
    }

    const ret = {} as any;
    for (const k in obj) {
        const value = fn(obj[k], k);
        if (value != null) {
            ret[k as any] = value;
        }
    }
    return ret;
};

interface OmitFn {
    <T>(obj:T, attrs:[]): T,
    <T, K extends keyof T>(obj:T, attrs: K[]): Omit<T,K>,
    (obj:any, attrs: string[]): any,
}
export const omit:OmitFn = (obj:any, attrs: string[]): any => {
    if (obj == null) {
        return null;
    }

    const ret = {};
    for (const k in obj) {
        if (!~attrs.indexOf(k as any)) {
            ret[k] = obj[k];
        }
    }
    return ret;
};

export function filterValues(o: object, fn: (a: any, k: string) => any) {
    let ret = {};
    for (const k in o) {
        if (fn(o[k], k)) {
            ret[k] = o[k];
        }
    }
    return ret;
}

export function oMapToArr<T>(o: T): Array<T[keyof T]>;
export function oMapToArr<T, ReturnItem>(o:T, fn:(value: T[typeof key], key: keyof T) => ReturnItem): ReturnItem[]
export function oKeyMapToArr<T>(o: T): Array<string> {
    return Object.keys(o);
}
export function oMapToArr<T>(o:T, fn:(value:T[typeof key], key:keyof T) => any = v=>v, sortBy:string = undefined) {
    let ret = [];
    if(sortBy) {
        let ret = [];
        const sortedKeys = Object.keys(o).sort((a, b) => o[a][sortBy] - o[b][sortBy]);
        for (const k of sortedKeys) {
            ret.push(fn(o[k], k));
        }
        return ret;
    }
    for (const k in o) {
        ret.push(fn(o[k], k));
    }
    return ret;
}

export function oMapToArrWithIndex(o: object, fn: (value: any, key: string, index: number) => any = v=>v) {
    let ret = [];
    let i = 0
    for (const k in o) {
        ret.push(fn(o[k], k, i));
        i++;
    }
    return ret;
}

export function arrMapToO(arr: any[], getValue:(k: any, i:number) => any, getKey: (k:any, i:number) => any = v=>v): {} {
    let ret = {};
    for (let i = 0; i < arr.length; i++) {
        const e = arr[i];
        ret[getKey(e, i)] = getValue(e, i);
    }
    return ret;
}

export function arrMapToO2(arr: any[], getValue:(k: any) => any, getKey: (e: any) => any = v=>v): {} {
    if (arr == null) {
        return arr;
    }
    let ret = {};
    for (const e of arr) {
        if( getValue(e) && Object.keys(getValue(e)).length > 0 ){
            ret[getKey(e)] = getValue(e);
        }
    }
    return ret;
}

export function isEmpty(o: object | null) {
    if (o == null) {
        return true;
    }
    return Object.keys(o).length === 0;
}
export function isNotEmpty(o: object | null) {
    return !isEmpty(o);
}

export function isEmptyContainer(container) {
    if (Array.isArray(container)) {
        return container.length === 0;
    } else if (typeof container === 'object' && container !== null) {
        return Object.keys(container).length === 0;
    }
    return true;
}

export const equalDeep = (o1: {}, o2: {}) => {
    if (o1 === o2) {
        return true;
    }
    if (o1 == null && o2 == null) {
        return true;
    }

    if (o1 == null || o2 == null) {
        return false;
    }

    if (typeof o1 === "object" && typeof o2 === "object") {
        for (const k in o1) {
            if (!equalDeep(o1[k], o2[k])) {
                return false;
            }
        }
        for (const k in o2) {
            if (o1[k] === undefined && o2[k] !== undefined) {
                return false;
            }
        }
        return true;
    }

    return false;
};

export const deepMerge = (o1, o2) => {
    if (!o1) {
        return o2;
    } else if (!o2) {
        return o1;
    }

    let ret = {};
    for (const o1Key in o1) {
        if (o2.hasOwnProperty(o1Key)) {
            ret[o1Key] = deepMerge(o1[o1Key], o2[o1Key]);
        } else {
            ret[o1Key] = o1[o1Key];
        }
    }
    for (const o2Key in o2) {
        if (!o1.hasOwnProperty(o2Key)) {
            ret[o2Key] = o2[o2Key];
        }
    }
    return ret;
};

export const splitKeys = (obj: object, fn: (k: string) => any): object[] => {
    const trues = {};
    const falses = {};
    for (const k in obj) {
        if (fn(k)) {
            trues[k] = obj[k];
        } else {
            falses[k] = obj[k];
        }
    }
    return [trues, falses];
};


export const deleteAll = (obj: object) => {
    for (const k in obj) {
        delete obj[k];
    }
};

export const keepOnly = (o: any, attrs: string[]): any => {
    if (o == null) {
        return o;
    }
    const ret = {};
    for (const k in o) {
        if (attrs.indexOf(k) > -1) {
            ret[k] = o[k];
        }
    }
    return ret;
};

export const singleKey = (o) => {
    if (o == null) {
        return null;
    }
    for (const key in o) {
        return key;
    }
};
export const singleValue = (o) => {
    if (o == null) {
        return null;
    }
    for (const k in o) {
        return o[k];
    }
};

export const resolveKey = (value, map) => {
    for (const key in map) {
        if (map[key] === value) {
            return key;
        }
    }
};

export const fromEntries = (pairs) => (
    Array.from(pairs).reduce(
        (acc, [key, value]) => Object.assign(acc, { [key]: value }),
        {},
));

interface EntriesFn {
    <T>(obj:T): Types.Common.Utils.Objects.Entries<T>
}
export const entries:EntriesFn = Object["entries"] ?? (
    (obj) => Object.keys(obj).map((key) => [key, obj[key]])
);

export const values: <T>(any:T) => T[keyof T][] = Object["values"] ?? (
    (obj) => Object.keys(obj).map(e => obj[e])
);

export const reduceValues = (objs, reduce) => {
    if (!objs?.length) {
        return null;
    }
    let ret = objs[0];
    for (let i = 1; i < objs.length; i++) {
        const o = objs[i];
        ret = reduceValues1(ret, o, reduce);
    }
    return ret;
};

export const reduceValues1 = (o1, o2, reduce) => {
    let ret = {};
    for (const k in o1) {
        ret[k] = o2.hasOwnProperty(k) ? (
            reduce(o1[k], o2[k])
        ) : (
            o1[k]
        );
    }

    for (const k in o2) {
        if (o1.hasOwnProperty(k)) {
            continue;
        }
        ret[k] = o2[k];
    }
    return ret;
}

/**
 * Remove keys in A that exist in B
 */
export const diffKeys = <A extends Record<any, any>, B extends Record<any, any>>(
    a: A,
    b: B,
): keyof any extends keyof B ? Partial<A> : Omit<A, keyof B> => (
    Object.keys(a).reduce<any>((result, key) => (
        key in b
        ? result
        : {...result, [key]: a[key]}
    ), {} as any)
);

/**
 * Remove key-value pairs in A that are identical in B
 */
export const diffEntries = <A extends Record<any, any>, B extends Record<any, any>>(
    a: A,
    b: B,
): keyof any extends keyof B ? Partial<A> : Omit<A, keyof B> & Partial<A> => (
    Object.fromEntries(Object.entries(a).reduce<any>((entries, [key, value]) => (
        key in b && value === b[key]
        ? entries
        : [...entries, [key, value]]
    ), [])) as any
);

/**
 * Create a filter to check if an object has a key, and its value is not undefined
 * null and other falsey values will still pass this test
 */
export const keyFilter = <K extends string>(key: K) => (
    <T extends {[k in K]?: any}>(obj: T): obj is T & {[k in K]: Exclude<T[k], undefined>} => obj[key] !== undefined
);

/**
 * Create a filter from an object that tests if a value exists as a key in said object
 */
export const isKeyOf = <T extends object>(obj: T) => (k: string | number | symbol): k is keyof T => k in obj;

